import { CalendarCheck, ClipboardList, Clock, HeartHandshake, Home, PawPrint, Users } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";

const CardShell = ({ children, className = "", testid }) => (
    <div
        data-testid={testid}
        className={`group relative flex flex-col overflow-hidden rounded-3xl bg-creamlight p-7 sm:p-8 ring-1 ring-teal/10 transition-shadow duration-500 hover:shadow-xl hover:shadow-teal/10 ${className}`}
    >
        {children}
    </div>
);

const IconBadge = ({ icon: Icon, coral = false }) => (
    <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${coral ? "bg-coral/10 text-coral" : "bg-teal/10 text-teal"}`}>
        <Icon className="h-6 w-6" />
    </span>
);

const CardText = ({ title, text }) => (
    <>
        <h3 className="mt-5 font-serif text-xl sm:text-2xl font-semibold text-charcoal">{title}</h3>
        <p className="mt-2.5 text-sm sm:text-base text-charcoal/70 leading-relaxed">{text}</p>
    </>
);

export const Services = () => (
    <section id="servicii" data-testid="services-section" className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
                testid="services-heading"
                eyebrow="Servicii"
                title="Ajutorul practic, organizat cap-coadă"
                text="De la pregătirea casei înainte de externare până la vizitele recurente — aceiași doi oameni cunosc situația și urmăresc fiecare sarcină convenită."
            />

            <div className="mt-12 grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-12">
                <Reveal className="md:col-span-7" delay={0}>
                    <CardShell testid="service-card-spital" className="h-full !p-0">
                        <div className="h-56 sm:h-64 overflow-hidden">
                            <img
                                src="https://images.pexels.com/photos/12933002/pexels-photo-12933002.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="O casă caldă și ordonată, pregătită pentru întoarcere"
                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>
                        <div className="p-7 sm:p-8">
                            <IconBadge icon={Home} />
                            <CardText
                                title="Din spital acasă"
                                text="Pregătim casa înainte de externare: măsurători, trasee eliberate, obiecte la îndemână, aprovizionare și primirea echipamentelor. Coordonăm transportul și primele zile organizate."
                            />
                        </div>
                    </CardShell>
                </Reveal>

                <Reveal className="md:col-span-5" delay={0.08}>
                    <CardShell testid="service-card-programat" className="h-full">
                        <IconBadge icon={CalendarCheck} coral />
                        <CardText
                            title="Ajutor programat"
                            text="16 ore pe lună de gospodărie și sarcini convenite: mese, rufe, ordine ușoară. Aceeași persoană, același program, aceeași grijă."
                        />
                    </CardShell>
                </Reveal>

                <Reveal className="md:col-span-5" delay={0}>
                    <CardShell testid="service-card-vizite" className="h-full">
                        <IconBadge icon={Users} />
                        <CardText
                            title="Vizite & însoțire"
                            text="Vizită și conversație pentru seniori, ajutor digital, organizarea programărilor și însoțire la ele — cu timpul de așteptare inclus."
                        />
                    </CardShell>
                </Reveal>

                <Reveal className="md:col-span-7" delay={0.08}>
                    <CardShell testid="service-card-animale" className="h-full !p-0 md:flex-row md:items-stretch md:flex">
                        <div className="h-52 md:h-auto md:w-2/5 overflow-hidden">
                            <img
                                src="https://images.pexels.com/photos/38034768/pexels-photo-38034768.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="Plimbarea câinelui, parte din grija de zi cu zi"
                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>
                        <div className="p-7 sm:p-8 md:w-3/5">
                            <IconBadge icon={PawPrint} coral />
                            <CardText
                                title="Îngrijirea animalelor"
                                text="Plimbat, hrană, apă, litieră — grupate pe traseul vizitelor, ca nimeni din casă să nu rămână uitat."
                            />
                        </div>
                    </CardShell>
                </Reveal>

                <Reveal className="md:col-span-4" delay={0}>
                    <CardShell testid="service-card-temporar" className="h-full">
                        <IconBadge icon={Clock} />
                        <CardText
                            title="Ajutor temporar"
                            text="Minimum 2 ore pe vizită, cât și când trebuie — ideal în perioadele de recuperare."
                        />
                    </CardShell>
                </Reveal>

                <Reveal className="md:col-span-4" delay={0.08}>
                    <CardShell testid="service-card-familia" className="h-full">
                        <IconBadge icon={HeartHandshake} coral />
                        <CardText
                            title="Familia organizată"
                            text="Coordonare lunară pentru copilul adult care locuiește departe. Știți mereu ce se întâmplă."
                        />
                    </CardShell>
                </Reveal>

                <Reveal className="md:col-span-4" delay={0.16}>
                    <CardShell testid="service-card-administrativ" className="h-full">
                        <IconBadge icon={ClipboardList} />
                        <CardText
                            title="Viață administrativă"
                            text="Programări, scanări, colete, retururi și accesul meseriașilor — cu mandat limitat, transparent."
                        />
                    </CardShell>
                </Reveal>
            </div>
        </div>
    </section>
);
